import type { Map } from 'leaflet';
import { createGlobalState } from 'react-use';
import { useRouter } from 'next/router';
import { useEffect, useRef, useCallback } from 'react';
import { isMapPage, isCurrentPageMap } from '../../utils/mapRouteUtils';

const useGlobalMapState = createGlobalState<Map | undefined>();

const useMap = (): [Map | undefined, (map: Map | undefined) => void] => {
  const [map, setMap] = useGlobalMapState();
  const router = useRouter();
  const prevRouteRef = useRef<string>('');
  const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to perform map cleanup
  const cleanupMap = useCallback((mapInstance: Map) => {
    try {
      // Remove all event listeners first
      if (mapInstance && typeof mapInstance.off === 'function') {
        mapInstance.off();
      }

      // Clear all layers
      if (mapInstance && typeof mapInstance.eachLayer === 'function') {
        mapInstance.eachLayer((layer: any) => {
          if (layer && mapInstance.hasLayer && mapInstance.hasLayer(layer)) {
            mapInstance.removeLayer(layer);
          }
        });
      }

      // Remove the map from DOM if it exists
      if (mapInstance && typeof mapInstance.remove === 'function') {
        mapInstance.remove();
      }
    } catch (_err) {
      // Silently handle errors during cleanup
    }
  }, []);

  // Helper function to clear pending cleanup timeout
  const clearPendingCleanup = useCallback(() => {
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }
  }, []);

  // Check if current page is a map page
  const isCurrentPageMapPage = isCurrentPageMap(router.asPath);

  // More intelligent cleanup - only when truly leaving map section
  useEffect(() => {
    const handleRouteChangeStart = (url: string) => {
      const newIsMapPage = isMapPage(url);

      // Clear any pending cleanup if we're navigating to another map page
      if (newIsMapPage) {
        clearPendingCleanup();
      }
    };

    const handleRouteChangeComplete = (url: string) => {
      const previousIsMapPage = isMapPage(prevRouteRef.current);
      const newIsMapPage = isMapPage(url);

      // Only cleanup if we're going from a map page to a non-map page
      if (previousIsMapPage && !newIsMapPage && map) {
        // Delay cleanup to ensure React has finished rendering
        cleanupTimeoutRef.current = setTimeout(() => {
          cleanupMap(map);
          setMap(undefined);
          cleanupTimeoutRef.current = null;
        }, 300); // Longer delay to ensure stability
      }

      // Update previous route reference
      prevRouteRef.current = url;
    };

    // Set initial route
    if (prevRouteRef.current === '') {
      prevRouteRef.current = router.asPath;
    }

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      clearPendingCleanup();
    };
  }, [router.events, router.asPath, map, setMap, cleanupMap, clearPendingCleanup]);

  // Final cleanup on hook unmount (when all map components are gone)
  useEffect(
    () => () => {
      clearPendingCleanup();

      // Only cleanup if we're not on a map page
      if (!isCurrentPageMapPage && map) {
        cleanupMap(map);
        setMap(undefined);
      }
    },
    [isCurrentPageMapPage, map, setMap, cleanupMap, clearPendingCleanup],
  );

  return [map, setMap];
};

export default useMap;
